import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { useCurrency } from '@/Hooks/useCurrency';
import { apiMessage } from '../helpers';
import { planLimitRefusal, type PlanLimitRefusal } from '../planLimits';
import type { Branch } from '../types';
import CurrencySelect from './CurrencySelect';
import PlanLimitNotice from './PlanLimitNotice';

/** Shown under the picker so the chosen minor unit is visible before saving. */
const PREVIEW_AMOUNT = '1234.5678';

interface Props {
  open: boolean;
  branch: Branch | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  name: string;
  code: string;
  address: string;
  phone: string;
  timezone: string;
  /** Registry id. `''` means "inherit the organization's base currency". */
  currencyId: string;
  latitude: string;
  longitude: string;
  wifiSsid: string;
  wifiPassword: string;
  isActive: boolean;
}

const BLANK: FormState = {
  name: '',
  code: '',
  address: '',
  phone: '',
  timezone: 'UTC',
  currencyId: '',
  latitude: '',
  longitude: '',
  wifiSsid: '',
  wifiPassword: '',
  isActive: true,
};

/** One labelled band of the form. The design draws these as separate cards on a
 *  routed page; inside a dialog the same grouping is a heading and a hairline. */
function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4 border-t pt-4 first:border-t-0 first:pt-0">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-ink-muted">{hint}</p>}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

/**
 * Branch CRUD including the guest Wi-Fi credentials the QR app renders (§19).
 *
 * Laid out as the Branch Settings frame groups it — general info, then location,
 * then guest Wi-Fi — but kept as a dialog rather than a `/settings/branches/:id`
 * route, and deliberately missing four of the frame's five tabs. Operating
 * hours, per-branch payment methods, branch tax config and integrations have no
 * model, no DTO and no endpoint between them; drawing the tabs would promise
 * four screens that cannot save. `Email` and `Manager` are the same story one
 * field down — neither is on `Branch` and neither is in `CreateBranchDto`, so
 * with `forbidNonWhitelisted` sending either 400s the whole form.
 *
 * Nothing here binds a service charge. `Area.serviceChargeRate` exists on the
 * model and on the area DTO and is read by nothing, so a control bound to it
 * would save, report success and change no money anywhere — the one failure the
 * frame avoids by drawing no such control. Keep it that way.
 */
export default function BranchFormDialog({ open, branch, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { currencies, tenantCode } = useCurrency();
  const [form, setForm] = useState<FormState>(BLANK);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  /** The plan ceiling this venue just walked into, if that is what refused the save. */
  const [planLimit, setPlanLimit] = useState<PlanLimitRefusal | null>(null);

  // Declared AFTER `form` — reading it above the useState is a temporal dead
  // zone error that crashes the dialog on mount, not a lint nit.
  // Previews the CHOSEN currency, not the screen's active one: the whole point
  // is to see this branch's minor unit before committing to it.
  const selectedCode = currencies.find((c) => c.id === form.currencyId)?.code ?? tenantCode;

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setPlanLimit(null);
    setForm(
      branch
        ? {
            name: branch.name,
            code: branch.code,
            address: branch.address ?? '',
            phone: branch.phone ?? '',
            timezone: branch.timezone || 'UTC',
            currencyId: branch.currencyId ?? '',
            latitude: branch.latitude === null ? '' : String(branch.latitude),
            longitude: branch.longitude === null ? '' : String(branch.longitude),
            wifiSsid: branch.wifiSsid ?? '',
            wifiPassword: branch.wifiPassword ?? '',
            isActive: branch.isActive,
          }
        : BLANK,
    );
  }, [open, branch]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = t('settings.validation.required');
    if (!form.code.trim()) next.code = t('settings.validation.required');
    for (const key of ['latitude', 'longitude'] as const) {
      if (form[key] && !Number.isFinite(Number(form[key]))) {
        next[key] = t('settings.validation.number');
      }
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    // Optional strings are sent even when empty — that is how a Wi-Fi password
    // gets cleared. Only the coordinates are omitted when blank, since the DTO
    // validates them as numbers.
    const body = {
      name: form.name.trim(),
      code: form.code.trim(),
      address: form.address.trim(),
      phone: form.phone.trim(),
      timezone: form.timezone.trim() || 'UTC',
      // The registry id is the authority; the API syncs the denormalized code.
      // Omitted when blank so the branch inherits the organization's currency
      // rather than being pinned to a guess.
      ...(form.currencyId ? { currencyId: form.currencyId } : { currencyId: null }),
      wifiSsid: form.wifiSsid.trim(),
      wifiPassword: form.wifiPassword.trim(),
      isActive: form.isActive,
      ...(form.latitude ? { latitude: Number(form.latitude) } : {}),
      ...(form.longitude ? { longitude: Number(form.longitude) } : {}),
    };

    setSaving(true);
    setPlanLimit(null);
    try {
      if (branch) {
        await api.patch(`/branches/${branch.id}`, body);
        toast.success(t('settings.branches.updated'));
      } else {
        await api.post('/branches', body);
        toast.success(t('settings.branches.created'));
      }
      onSaved();
      onClose();
    } catch (error) {
      // The plan ceiling gets a panel that stays and names the way out, not a toast that
      // shows the server's English and then leaves. Everything else keeps the toast.
      const refusal = planLimitRefusal(error);
      setPlanLimit(refusal);
      if (!refusal) toast.error(apiMessage(error, t('settings.errors.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={branch ? t('settings.branches.editTitle') : t('settings.branches.createTitle')}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving ? t('settings.common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {planLimit && <PlanLimitNotice refusal={planLimit} />}

        <Section
          title={t('settings.branches.generalSection')}
        >
          <Field label={t('settings.branches.name')} required error={errors.name}>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus />
          </Field>
          <Field label={t('settings.branches.code')} required error={errors.code}>
            <Input
              value={form.code}
              dir="ltr"
              className="text-start"
              onChange={(e) => set('code', e.target.value.toUpperCase())}
            />
          </Field>
          <Field label={t('settings.branches.address')}>
            <Input value={form.address} onChange={(e) => set('address', e.target.value)} />
          </Field>
          <Field label={t('settings.branches.phone')}>
            <Input value={form.phone} dir="ltr" onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label={t('settings.branches.timezone')}>
            <Input
              value={form.timezone}
              dir="ltr"
              onChange={(e) => set('timezone', e.target.value)}
            />
          </Field>
          <Field
            label={t('settings.branches.currency')}
            hint={
              form.currencyId
                ? formatCurrency(PREVIEW_AMOUNT, selectedCode)
                : t('settings.branches.currencyInherited', { code: tenantCode })
            }
          >
            <CurrencySelect
              currencies={currencies}
              value={form.currencyId}
              inheritLabel={t('settings.branches.currencyInheritOption', { code: tenantCode })}
              onChange={(currencyId) => set('currencyId', currencyId)}
              aria-label={t('settings.branches.currency')}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={form.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
            />
            {t('settings.branches.isActive')}
          </label>
        </Section>

        <Section
          title={t('settings.branches.locationSection')}
          hint={t('settings.branches.locationHint')}
        >
          <Field label={t('settings.branches.latitude')} error={errors.latitude}>
            <Input
              value={form.latitude}
              dir="ltr"
              inputMode="decimal"
              onChange={(e) => set('latitude', e.target.value)}
            />
          </Field>
          <Field label={t('settings.branches.longitude')} error={errors.longitude}>
            <Input
              value={form.longitude}
              dir="ltr"
              inputMode="decimal"
              onChange={(e) => set('longitude', e.target.value)}
            />
          </Field>
        </Section>

        <Section
          title={t('settings.branches.wifiSection')}
          hint={t('settings.branches.wifiHint')}
        >
          <Field label={t('settings.branches.wifiSsid')}>
            <Input
              value={form.wifiSsid}
              dir="ltr"
              onChange={(e) => set('wifiSsid', e.target.value)}
            />
          </Field>
          <Field label={t('settings.branches.wifiPassword')}>
            <Input
              value={form.wifiPassword}
              dir="ltr"
              onChange={(e) => set('wifiPassword', e.target.value)}
            />
          </Field>
        </Section>
      </div>
    </Dialog>
  );
}
